import {
  createAction,
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
  appLogout,
  injectSlice,
  persistReducer,
} from '@bonanza-social/toolkit/state';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import { StatusService } from '../../services';
import { StatusResponseType, StatusState } from './status.types';
import { RewardsService } from '@bonanza-social/toolkit/services';

const initialState: StatusState = {
  data: undefined,
  dataState: 'idle',
  claimingRewardIds: [],
  claimAllRewardsDataState: 'idle',
};

export const statusChangedLegacy = createAction('status/changedLegacy');

export const statusChanged = createAction('status/changed');

export const statusBoxClaimed = createAction('status/boxClaimed');

export const fetchStatus = createAsyncThunk(
  'status/fetch',
  async (_, thunkApi) => {
    const response = await StatusService.getStatus();

    thunkApi.dispatch(updateStatus(response));
  },
);

export const acknowledgeStatus = createAsyncThunk(
  'status/acknowledge',
  async () => StatusService.acknowledgeStatus(),
);

export const claimStatusReward = createAsyncThunk(
  'status/claimBox',
  (rewardId: number) =>
    RewardsService.claimReward({ pathParams: { id: rewardId } }),
);

export const claimAllStatusRewards = createAsyncThunk(
  'status/claimAllBoxes',
  async () => StatusService.claimAllStatusRewards(),
);

const status = createSlice({
  name: 'status',
  initialState,
  reducers: {
    updateStatus(state, { payload }: PayloadAction<StatusResponseType>) {
      const { nextPlayerStatus } = payload;

      state.data = {
        ...state.data,
        ...payload,
        // If a user moves from Sapphire to Diamond status, this field will not be present in the payload.
        // In that case, the state may still contain the outdated Diamond value, making it incorrect.
        nextPlayerStatus,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchStatus.pending, (state) => {
      state.dataState =
        state.dataState === 'fulfilled' ? 'refreshing' : 'pending';
    });

    builder.addCase(fetchStatus.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(fetchStatus.fulfilled, (state) => {
      state.dataState = 'fulfilled';
    });

    builder.addCase(claimAllStatusRewards.pending, (state) => {
      state.claimAllRewardsDataState = 'pending';
    });

    builder.addCase(claimAllStatusRewards.fulfilled, (state) => {
      state.claimAllRewardsDataState = 'fulfilled';

      if (state.data?.pendingRewards) {
        state.data.pendingRewards = [];
      }
    });

    builder.addCase(claimAllStatusRewards.rejected, (state) => {
      state.claimAllRewardsDataState = 'rejected';
    });

    builder.addCase(claimStatusReward.pending, (state, action) => {
      state.claimingRewardIds.push(action.meta.arg);
    });

    builder.addCase(claimStatusReward.fulfilled, (state, action) => {
      if (state.data?.pendingRewards) {
        state.data.pendingRewards = state.data?.pendingRewards.filter(
          ({ rewardId }) => rewardId !== action.meta.arg,
        );
      }
    });

    builder.addCase(appLogout, (state) => {
      Object.assign(state, initialState);
    });

    builder.addMatcher(claimStatusReward.settled, (state, action) => {
      state.claimingRewardIds = state.claimingRewardIds.filter(
        (id) => id !== action.meta.arg,
      );
    });
  },
});

export const { updateStatus } = status.actions;

const persistConfig = {
  key: 'status',
  storage: AsyncStorage,
  whitelist: ['data', 'points', 'currentLevel'],
};

const persistedReducer = persistReducer(persistConfig, status.reducer);

export const withStatus = injectSlice({
  reducerPath: 'status',
  reducer: persistedReducer,
});

export const selectStatus = withStatus.selector((state) => state.status.data);

export const selectIsStatusAboveAmethyst = withStatus.selector(
  (state) =>
    state.status.data?.playerStatus &&
    state.status.data?.playerStatus !== 'Member' &&
    state.status.data?.playerStatus !== 'Amethyst',
);

export const selectBoxCountTransmit = withStatus.selector((state) => {
  const claimedBoxesCount = state.status.data?.claimedBoxesCount ?? 0;

  const isStatusAboveAmethyst = selectIsStatusAboveAmethyst(state);

  return isStatusAboveAmethyst && claimedBoxesCount === 0;
});

export const selectStatusDataState = withStatus.selector(
  (state) => state.status.dataState,
);

export const selectClaimingRewardIds = withStatus.selector(
  (state) => state.status.claimingRewardIds,
);

export const selectIsClaimingReward = createSelector(
  [selectClaimingRewardIds, (_, rewardId) => rewardId],
  (ids, rewardId) => ids.indexOf(rewardId) !== -1,
);

export const selectIsClaimingAllRewards = withStatus.selector(
  (state) => state.status.claimAllRewardsDataState === 'pending',
);

export const selectIsNewLoyalty = withStatus.selector(
  (state) => state.status.data?.rewardStrategy === 'Auto',
);

declare global {
  interface LazyLoadedSlices {
    status: ReturnType<typeof persistedReducer>;
  }
}
